import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const MIME = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
  odt: "application/vnd.oasis.opendocument.text",
  zip: "application/zip",
};

const saveBlob = (data, mime, filename) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

const errorDetail = async (e) => {
  try {
    if (e?.response?.data instanceof Blob) {
      const txt = await e.response.data.text();
      try { return JSON.parse(txt).detail || txt; } catch { return txt; }
    }
    return e?.response?.data?.detail || e?.message || "Άγνωστο σφάλμα";
  } catch { return "Άγνωστο σφάλμα"; }
};

const filenameFrom = (headers, fallback) => {
  const m = (headers?.["content-disposition"] || "").match(/filename="?([^"]+)"?/);
  return m ? m[1] : fallback;
};

// Λήψη Πράξης Αναχρέωσης μιας υπόθεσης (επανέκδοση από αποθηκευμένα στοιχεία)
export const downloadReassignmentAct = async (caseId, format = "docx") => {
  try {
    const r = await axios.get(`${API}/reassignment/act/${caseId}?format=${format}`, { responseType: "blob" });
    saveBlob(r.data, MIME[format], filenameFrom(r.headers, `praxi_anaxreosis.${format}`));
    toast.success(`Πράξη ΑΝΑΧΡΕΩΣΗΣ (${format.toUpperCase()}) έτοιμη`);
  } catch (e) {
    toast.error(`Σφάλμα λήψης Πράξης ΑΝΑΧΡΕΩΣΗΣ: ${String(await errorDetail(e)).slice(0, 300)}`, { duration: 10000 });
  }
};

// Εκτέλεση Αναχρέωσης → ZIP πράξεων. Επιστρέφει {generated, firstAct, lastAct} ή throws.
export const runReassignment = async (payload) => {
  try {
    const r = await axios.post(`${API}/reassignment/run`, payload, { responseType: "blob" });
    saveBlob(r.data, MIME.zip, filenameFrom(r.headers, `anaxreosi_${payload.hearing_date}.zip`));
    return { generated: r.headers["x-generated"], firstAct: r.headers["x-first-act"], lastAct: r.headers["x-last-act"] };
  } catch (e) {
    throw new Error(String(await errorDetail(e)).slice(0, 600));
  }
};

// «Πράξη Σύνθεσης (ZIP)» για πινάκιο × δικάσιμο Πολυμελούς — με την ανατεθειμένη σύνθεση, χωρίς email
export const downloadSynthesisActs = async ({ pinakioLabel, hearingDate, startingActNumber, format = "docx", caseIds = [], issueDates = {} }) => {
  try {
    const r = await axios.post(`${API}/pretrial-tools/bulk-assignment-acts`, {
      court_type: "Πολυμελές", pinakio_label: pinakioLabel, hearing_date: hearingDate,
      starting_act_number: startingActNumber, format, auto_email: false, case_ids: caseIds, issue_dates: issueDates,
    }, { responseType: "blob" });
    saveBlob(r.data, MIME.zip, filenameFrom(r.headers, `praxeis_synthesis_${hearingDate}.zip`));
    toast.success(`Πράξεις Σύνθεσης: ${r.headers["x-generated"] || "?"} παράχθηκαν${r.headers["x-skipped"] && r.headers["x-skipped"] !== "0" ? ` · ${r.headers["x-skipped"]} παραλείφθηκαν` : ""}`);
    return true;
  } catch (e) {
    toast.error(`Σφάλμα Πράξεων Σύνθεσης: ${String(await errorDetail(e)).slice(0, 400)}`, { duration: 12000 });
    return false;
  }
};

// Πρόταση επόμενου αριθμού Πράξης Ορισμού/Σύνθεσης (ίδιο βιβλίο) για πινάκιο
export const suggestNextActNumber = async ({ courtType, year, pinakioLabel }) => {
  try {
    const r = await axios.get(`${API}/pretrial-sectors/next-act-number`, { params: { court_type: courtType, year, pinakio_label: pinakioLabel } });
    return Number.isInteger(r.data?.next) ? String(r.data.next) : "";
  } catch { return ""; }
};

// Γενική λήψη Πράξης από GET endpoint (Ορισμού Δικαστή/Σύνθεσης/Εισηγητή) με προαιρετικά params (act_number, issue_date…)
export const downloadActFromUrl = async (url, params = {}, format = "docx", label = "Πράξη") => {
  try {
    const r = await axios.get(url, { params: { ...params, format }, responseType: "blob" });
    const ct = r.headers?.["content-type"] || "";
    if (!ct.includes(MIME[format].split(";")[0])) {
      const txt = await r.data.text();
      let msg = txt;
      try { msg = JSON.parse(txt).detail || txt; } catch { /* keep */ }
      throw new Error(String(msg).slice(0, 300));
    }
    saveBlob(r.data, MIME[format], filenameFrom(r.headers, `praxi.${format}`));
    toast.success(`${label} (${format.toUpperCase()}) έτοιμη και αποθηκεύτηκε στην υπόθεση`);
    return true;
  } catch (e) {
    toast.error(`Σφάλμα έκδοσης: ${String(e?.message || await errorDetail(e)).slice(0, 300)}`, { duration: 10000 });
    return false;
  }
};
